(function() {
  if (customElements.get('look-builder-product')) return;

  /* ============================================================
     Helpers
     ============================================================ */

  /* Parse data-attrs d'une card → objet produit normalisé.
     Évite de stocker du JSON volumineux dans le DOM. */
  function parseProduct(card) {
    if (!card) return null;
    var multi = card.dataset.multiVariant === 'true';
    var variants = [];
    if (multi && card.dataset.variants) {
      try { variants = JSON.parse('[' + card.dataset.variants + ']'); }
      catch (e) { variants = []; }
    } else {
      variants = [{
        id: parseInt(card.dataset.variantId, 10),
        title: card.dataset.variantTitle || '',
        price: parseInt(card.dataset.variantPrice, 10) || 0,
        available: card.dataset.available === 'true'
      }];
    }
    return {
      id: parseInt(card.dataset.productId, 10),
      handle: card.dataset.charmHandle || card.dataset.chainHandle || '',
      title: card.dataset.productTitle || '',
      featured_image: card.dataset.image || '',
      available: card.dataset.available === 'true',
      multi_variant: multi,
      variants: variants
    };
  }

  /* requestAnimationFrame throttle — appelle fn au plus 1×/frame. */
  function rafThrottle(fn) {
    var pending = false;
    var lastArgs = null;
    var lastCtx = null;
    return function() {
      lastArgs = arguments;
      lastCtx = this;
      if (pending) return;
      pending = true;
      requestAnimationFrame(function() {
        pending = false;
        fn.apply(lastCtx, lastArgs);
      });
    };
  }

  function debounce(fn, ms) {
    var t;
    return function() {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function() { fn.apply(ctx, args); }, ms);
    };
  }

  /* Vibration tactile (mobile) — best effort, silencieux si non supporté. */
  function haptic(ms) {
    try {
      if (navigator && typeof navigator.vibrate === 'function') {
        navigator.vibrate(ms || 10);
      }
    } catch (e) {}
  }

  var HINT_SEEN_KEY = 'nyzia_lb_hint_seen';
  var STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours
  var HISTORY_MAX = 10;

  /* ============================================================
     Auto-trim transparent — détecte le bbox du contenu visible
     d'une image PNG (skip les zones transparentes). Évite le clic
     dans le vide + colle la croix au vrai coin du charm visuel.
     Caché par URL pour ne pas re-calculer. Fallback silencieux si CORS.
     ============================================================ */
  var BOUNDS_CACHE = {};

  function computeImageBounds(url) {
    if (!url) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(BOUNDS_CACHE, url)) {
      return Promise.resolve(BOUNDS_CACHE[url]);
    }
    return new Promise(function(resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        try {
          /* Downscale pour perf — 160px max suffit pour un bbox précis */
          var maxDim = 160;
          var scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          var w = Math.max(8, Math.round(img.width * scale));
          var h = Math.max(8, Math.round(img.height * scale));
          var c = document.createElement('canvas');
          c.width = w; c.height = h;
          var ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          var data = ctx.getImageData(0, 0, w, h).data;
          var minX = w, minY = h, maxX = -1, maxY = -1;
          for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
              /* alpha > 16 = pixel "visible" (seuil tolérant le bruit JPEG) */
              if (data[(y * w + x) * 4 + 3] > 16) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          var bounds = null;
          if (maxX >= minX && maxY >= minY) {
            bounds = {
              x: (minX / w) * 100,
              y: (minY / h) * 100,
              w: ((maxX - minX + 1) / w) * 100,
              h: ((maxY - minY + 1) / h) * 100
            };
          }
          BOUNDS_CACHE[url] = bounds;
          resolve(bounds);
        } catch (e) {
          /* CORS bloqué ou autre — silencieux, fallback comportement original */
          BOUNDS_CACHE[url] = null;
          resolve(null);
        }
      };
      img.onerror = function() {
        BOUNDS_CACHE[url] = null;
        resolve(null);
      };
      img.src = url;
    });
  }

  /* ============================================================
     Web Component
     ============================================================ */
  class LookBuilderProduct extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.parentProductId = this.dataset.parentProductId;
      this.parentVariantId = this.dataset.parentVariantId;
      this.parentTitle = this.dataset.parentTitle;
      this.parentImage = this.dataset.parentImage;
      this.parentBasePrice = 0;
      /* Limite max de charms — défini en setting Shopify, défaut 7 (impair).
         99 si non défini (fallback permissif pour ne rien casser). */
      this.maxCharms = parseInt(this.dataset.maxCharms, 10) || 99;

      this.state = {
        chainCardEl: null,    // référence à la card chaîne sélectionnée
        chainProduct: null,
        chainVariant: null,
        charms: [],
        note: ''
      };
      this.currentStep = 1;

      this.storageKey = 'nyzia_lb_' + this.parentProductId;
      this.historyStack = [];

      this._bindElements();
      this._bindEvents();
      this._readBasePrice();
      this._restoreFromStorage();
      this._refreshUndoButton();
    }

    _readBasePrice() {
      var totalText = (this.$recapTotalBtn && this.$recapTotalBtn.textContent) || '';
      var m = totalText.match(/[\d.,]+/);
      if (m) {
        this.parentBasePrice = Math.round(parseFloat(m[0].replace(/\s/g, '').replace(',', '.')) * 100);
      }
    }

    _bindElements() {
      this.$steps = this.querySelectorAll('[data-step-indicator]');
      this.$stepperFill = this.querySelector('[data-stepper-fill]');
      this.$panes = this.querySelectorAll('[data-pane]');
      this.$chainCards = this.querySelectorAll('.lbp__chain-card');
      this.$nextStep1 = this.querySelector('[data-next-step="2"]');
      this.$canvas = this.querySelector('[data-canvas]');
      this.$canvasChain = this.querySelector('[data-canvas-chain]');
      this.$canvasInstruction = this.querySelector('[data-canvas-instruction]');
      this.$charmsLayer = this.querySelector('[data-charms-layer]');
      this.$charmsCount = this.querySelector('[data-charms-count]');
      this.$chainSummary = this.querySelector('[data-chain-summary]');
      this.$clearCharms = this.querySelector('[data-clear-charms]');
      this.$recapList = this.querySelector('[data-recap-list]');
      this.$recapTotal = this.querySelector('[data-recap-total]');
      this.$recapTotalBtn = this.querySelector('[data-recap-total-btn]');
      this.$noteInput = this.querySelector('[data-note-input]');
      this.$submit = this.querySelector('[data-submit]');
      this.$error = this.querySelector('[data-error]');
      this.$success = this.querySelector('[data-success]');
      this.$charmsTabs = this.querySelectorAll('[data-charms-tab]');
      this.$charmsGrids = this.querySelectorAll('[data-charms-set]');
      this.$charmCards = this.querySelectorAll('.lbp__charm-card');
      this.$charmSearch = this.querySelector('[data-charm-search]');
      this.$searchEmpty = this.querySelector('[data-search-empty]');

      /* Phase 3 — éléments optionnels (peuvent ne pas exister selon version) */
      this.$undoBtn = this.querySelector('[data-undo]');
      this.$fullscreenBtn = this.querySelector('[data-fullscreen]');
      this.$loader = this.querySelector('[data-loader]');
      this.$fullscreenModal = this.querySelector('[data-fullscreen-modal]');
      this.$dragHint = this.querySelector('[data-drag-hint]');

      /* Recap améliorations */
      this.$recapBreakdown = this.querySelector('[data-recap-breakdown]');
      this.$recapDetails = this.querySelector('[data-recap-details]');
      this.$recapCount = this.querySelector('[data-recap-count]');
      this.$noteCounter = this.querySelector('[data-note-counter]');
      this.$noteSaved = this.querySelector('[data-note-saved]');
      this._lastDisplayedTotal = 0;
      this._noteSavedTimer = null;
    }

    _bindEvents() {
      var self = this;

      this.$chainCards.forEach(function(card) {
        card.addEventListener('click', function() { self._selectChain(card); });
      });

      this.querySelectorAll('[data-next-step]').forEach(function(btn) {
        btn.addEventListener('click', function() {
          self._goToStep(parseInt(btn.dataset.nextStep));
        });
      });
      this.querySelectorAll('[data-back-step]').forEach(function(btn) {
        btn.addEventListener('click', function() {
          self._goToStep(parseInt(btn.dataset.backStep));
        });
      });

      /* Steps cliquables — permet de revenir à une étape précédente */
      this.$steps.forEach(function(step) {
        step.addEventListener('click', function() {
          var target = parseInt(step.dataset.stepIndicator, 10);
          /* On autorise uniquement vers une étape déjà visitée (done) ou actuelle */
          if (target < self.currentStep || step.classList.contains('is-done')) {
            self._goToStep(target);
          }
        });
      });

      /* Tabs charms */
      this.$charmsTabs.forEach(function(tab) {
        tab.addEventListener('click', function() {
          var target = tab.dataset.charmsTab;
          self.$charmsTabs.forEach(function(t) { t.classList.toggle('is-active', t === tab); });
          self.$charmsGrids.forEach(function(g) {
            g.classList.toggle('is-active', g.dataset.charmsSet === target);
          });
          if (self.$charmSearch && self.$charmSearch.value) {
            self.$charmSearch.value = '';
            self._filterCharms('');
          }
          if (self.$searchEmpty) self.$searchEmpty.classList.add('is-hidden');
        });
      });

      /* Charm cards — click pour ajouter */
      this.$charmCards.forEach(function(card) {
        card.addEventListener('click', function() {
          if (card.classList.contains('is-out-of-stock')) return;
          var product = parseProduct(card);
          if (!product) return;
          var variant = (product.variants || []).find(function(v) { return v.available; }) || (product.variants || [])[0];
          if (!variant) return;
          var grid = card.closest('[data-charms-set]');
          var sizePercent = grid ? parseInt(grid.dataset.charmSize, 10) || 100 : 100;
          self._addCharmToCanvas(product, variant, sizePercent);
        });
      });

      /* Recherche live */
      if (this.$charmSearch) {
        this.$charmSearch.addEventListener('input', debounce(function(e) {
          self._filterCharms(e.target.value);
        }, 120));
      }

      /* Clear charms */
      if (this.$clearCharms) {
        this.$clearCharms.addEventListener('click', function() {
          if (self.state.charms.length === 0) return;
          if (!confirm('Effacer tous les charms ?')) return;
          self._pushHistory();
          self.state.charms = [];
          self._renderPlacedCharms();
          self._updateCanvasInfo();
          self._updateCharmCounts();
          self._saveToStorage();
        });
      }

      /* Note — compteur de caractères live + sauvegarde débouncée + feedback "Sauvegardé" */
      if (this.$noteInput) {
        this.$noteInput.addEventListener('input', function(e) {
          self.state.note = e.target.value;
          self._updateNoteCounter();
          self._debouncedSaveNote();
        });
        /* Init compteur au load (au cas où une note est restaurée du localStorage) */
        this._updateNoteCounter();
      }

      /* Détecte si l'utilisateur a manuellement toggle le <details> — empêche le "open auto" de surprendre */
      if (this.$recapDetails) {
        this.$recapDetails.addEventListener('toggle', function() {
          self.$recapDetails.setAttribute('data-user-toggled', '1');
        });
      }

      /* Submit */
      if (this.$submit) {
        this.$submit.addEventListener('click', function() { self._addToCart(); });
      }

      /* Phase 3 — Undo button + raccourci clavier */
      if (this.$undoBtn) {
        this.$undoBtn.addEventListener('click', function() { self._undo(); });
      }
      document.addEventListener('keydown', function(e) {
        if (self.currentStep !== 2) return;
        var isUndo = (e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey;
        if (isUndo) {
          var target = e.target;
          if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
          e.preventDefault();
          self._undo();
        }
      });

      /* Phase 3 — Fullscreen */
      if (this.$fullscreenBtn) {
        this.$fullscreenBtn.addEventListener('click', function() { self._openFullscreen(); });
      }
      if (this.$fullscreenModal) {
        this.$fullscreenModal.addEventListener('click', function(e) {
          /* closest() pour gérer le clic sur le SVG enfant du bouton de fermeture
             (e.target = <svg> ou <line> sinon, et hasAttribute renvoyait false). */
          var isCloseBtn = e.target.closest && e.target.closest('[data-fullscreen-close]');
          if (e.target === self.$fullscreenModal || isCloseBtn) {
            self._closeFullscreen();
          }
        });
      }
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self.$fullscreenModal && !self.$fullscreenModal.hidden) {
          self._closeFullscreen();
        }
      });
    }

    /* ============================================================
       LocalStorage — persistance composition
       ============================================================ */
    _saveToStorage() {
      try {
        var data = {
          ts: Date.now(),
          chainProductId: this.state.chainProduct ? this.state.chainProduct.id : null,
          chainVariantId: this.state.chainVariant ? this.state.chainVariant.id : null,
          note: this.state.note || '',
          charms: this.state.charms.map(function(c) {
            return {
              productId: c.product.id,
              variantId: c.variant.id,
              x: c.x, y: c.y, size: c.size
            };
          })
        };
        localStorage.setItem(this.storageKey, JSON.stringify(data));
      } catch (e) { /* quota / private mode → silencieux */ }
    }

    _restoreFromStorage() {
      try {
        var raw = localStorage.getItem(this.storageKey);
        if (!raw) return;
        var data = JSON.parse(raw);
        if (!data || !data.ts) return;
        if (Date.now() - data.ts > STORAGE_TTL_MS) {
          localStorage.removeItem(this.storageKey);
          return;
        }

        /* Restaure la chaîne */
        if (data.chainProductId) {
          var self = this;
          var card = Array.prototype.slice.call(this.$chainCards).find(function(c) {
            return parseInt(c.dataset.productId, 10) === data.chainProductId;
          });
          if (card) {
            this._selectChain(card, /* silent */ true);
          }
        }

        /* Restaure la note + update du compteur */
        if (data.note && this.$noteInput) {
          this.$noteInput.value = data.note;
          this.state.note = data.note;
          this._updateNoteCounter();
        }

        /* Restaure les charms — différé jusqu'au step 2 */
        this._pendingCharmRestore = (data.charms || []);
      } catch (e) {}
    }

    _clearStorage() {
      try { localStorage.removeItem(this.storageKey); } catch (e) {}
    }

    _applyPendingCharms() {
      if (!this._pendingCharmRestore || this._pendingCharmRestore.length === 0) return;
      var self = this;
      this._pendingCharmRestore.forEach(function(stored) {
        /* Cherche la card pour récupérer le produit complet */
        var card = self.querySelector('.lbp__charm-card[data-product-id="' + stored.productId + '"]');
        if (!card) return;
        var product = parseProduct(card);
        if (!product) return;
        var variant = (product.variants || []).find(function(v) { return v.id === stored.variantId; })
                   || (product.variants || []).find(function(v) { return v.available; })
                   || (product.variants || [])[0];
        if (!variant) return;
        var id = 'c-' + Date.now() + '-' + Math.floor(Math.random() * 100000).toString(36);
        self.state.charms.push({
          id: id,
          product: product,
          variant: variant,
          x: stored.x, y: stored.y, size: stored.size
        });
      });
      this._pendingCharmRestore = null;
      this._renderPlacedCharms();
      this._updateCanvasInfo();
      this._updateCharmCounts();
    }

    /* ============================================================
       Undo — historique des états
       ============================================================ */
    _snapshot() {
      return JSON.stringify({
        charms: this.state.charms.map(function(c) {
          return {
            productId: c.product.id,
            variantId: c.variant.id,
            x: c.x, y: c.y, size: c.size
          };
        })
      });
    }

    _pushHistory() {
      this.historyStack.push(this._snapshot());
      if (this.historyStack.length > HISTORY_MAX) this.historyStack.shift();
      this._refreshUndoButton();
    }

    _undo() {
      if (this.historyStack.length === 0) return;
      var snap = this.historyStack.pop();
      try {
        var data = JSON.parse(snap);
        var self = this;
        this.state.charms = (data.charms || []).map(function(stored) {
          var card = self.querySelector('.lbp__charm-card[data-product-id="' + stored.productId + '"]');
          var product = card ? parseProduct(card) : null;
          if (!product) return null;
          var variant = (product.variants || []).find(function(v) { return v.id === stored.variantId; })
                     || (product.variants || [])[0];
          if (!variant) return null;
          return {
            id: 'c-' + Date.now() + '-' + Math.floor(Math.random() * 100000).toString(36),
            product: product,
            variant: variant,
            x: stored.x, y: stored.y, size: stored.size
          };
        }).filter(Boolean);
        this._renderPlacedCharms();
        this._updateCanvasInfo();
        this._updateCharmCounts();
        this._saveToStorage();
        this._refreshUndoButton();
        haptic(8);
      } catch (e) {}
    }

    _refreshUndoButton() {
      if (!this.$undoBtn) return;
      this.$undoBtn.disabled = this.historyStack.length === 0;
      this.$undoBtn.setAttribute('aria-disabled', this.historyStack.length === 0 ? 'true' : 'false');
    }

    /* ============================================================
       Étape 1 — Choix de la chaîne
       ============================================================ */
    _selectChain(card, silent) {
      if (card.classList.contains('is-out-of-stock')) return;
      var product = parseProduct(card);
      if (!product) return;
      this.state.chainCardEl = card;
      this.state.chainProduct = product;

      this.$chainCards.forEach(function(c) {
        c.classList.toggle('is-active', c === card);
      });

      var variant = (product.variants && product.variants.length > 0)
        ? (product.variants.find(function(v) { return v.available; }) || product.variants[0])
        : null;
      this.state.chainVariant = variant && variant.available ? variant : null;

      if (this.$nextStep1) this.$nextStep1.disabled = !this.state.chainVariant;
      if (!silent) {
        this._saveToStorage();
        haptic(8);
      }
    }

    _filterCharms(query) {
      var self = this;
      query = (query || '').trim().toLowerCase();
      var activeGrid = this.querySelector('.lbp__charms-grid.is-active');
      if (!activeGrid) return;
      var cards = activeGrid.querySelectorAll('.lbp__charm-card');
      var visibleCount = 0;
      cards.forEach(function(card) {
        var name = card.dataset.charmName || '';
        var match = !query || name.indexOf(query) !== -1;
        card.classList.toggle('is-hidden', !match);
        if (match) visibleCount++;
      });
      if (self.$searchEmpty) {
        self.$searchEmpty.classList.toggle('is-hidden', visibleCount > 0);
      }
    }

    /* ============================================================
       Étape 2 — Canvas + drag libre
       ============================================================ */
    _setupCanvas() {
      if (!this.state.chainProduct || !this.$canvasChain) return;
      var img = this.state.chainProduct.featured_image;
      if (img) {
        this.$canvasChain.src = img;
        this.$canvasChain.alt = this.state.chainProduct.title;
      } else {
        this.$canvasChain.removeAttribute('src');
      }
      /* Si des charms étaient à restaurer depuis localStorage, on les pose maintenant */
      this._applyPendingCharms();
      this._renderPlacedCharms();
      this._updateCanvasInfo();
      this._updateCharmCounts();
    }

    /* Positionne le charm en cercle autour du centre (évite l'empilement). */
    _smartPosition(n) {
      if (n === 0) return { x: 50, y: 50 };
      var radius = Math.min(22, 6 + n * 1.8);
      var angle = ((n - 1) * (360 / 7)) * (Math.PI / 180);
      return {
        x: 50 + radius * Math.cos(angle),
        y: 50 + radius * Math.sin(angle)
      };
    }

    _addCharmToCanvas(product, variant, sizePercent) {
      if (!variant) return;
      /* Bloque l'ajout si limite atteinte. Feedback UI + vibration "erreur". */
      if (this.state.charms.length >= this.maxCharms) {
        this._showError('Tu as atteint le nombre maximum de ' + this.maxCharms + ' charm' + (this.maxCharms > 1 ? 's' : '') + ' pour ce bijou.');
        haptic([20, 40, 20]); /* pattern court-pause-court = "erreur" tactile */
        return;
      }
      this._pushHistory();

      var id = 'c-' + Date.now() + '-' + Math.floor(Math.random() * 100000).toString(36);
      var n = this.state.charms.length;
      var pos = this._smartPosition(n);

      var pct = parseInt(sizePercent, 10);
      if (isNaN(pct)) pct = 100;
      pct = Math.max(25, Math.min(100, pct));
      /* Mapping setting (25-100%) → taille charm en % du canvas.
         Mobile : 25→16%, 100→52% — généreux pour compenser le canvas étroit.
         Desktop : facteur ×0.8 — le canvas étant plus large en absolu, les
         charms paraissent disproportionnés s'ils gardent les mêmes ratios.
         Affecte uniquement les nouveaux ajouts (les charms existants stockés
         dans localStorage gardent leur taille). */
      var canvasSize = 16 + (pct - 25) * 0.48;
      var isDesktop = window.matchMedia && window.matchMedia('(min-width: 901px)').matches;
      if (isDesktop) canvasSize *= 0.8;

      this.state.charms.push({
        id: id,
        product: product,
        variant: variant,
        x: pos.x,
        y: pos.y,
        size: canvasSize
      });
      /* Marque ce charm comme fraîchement ajouté → _renderPlacedCharms
         déclenche is-selected pendant 3s pour montrer la croix (= signal
         "tu peux supprimer" sans polluer l'UI en permanence sur mobile). */
      this._lastAddedCharmId = id;
      this._renderPlacedCharms();
      this._updateCanvasInfo();
      this._updateCharmCounts(product.id);
      this._saveToStorage();
      this._maybeShowDragHint();
      haptic(12);
    }

    _removeCharm(id) {
      this._pushHistory();
      var removed = this.state.charms.find(function(c) { return c.id === id; });
      this.state.charms = this.state.charms.filter(function(c) { return c.id !== id; });
      this._renderPlacedCharms();
      this._updateCanvasInfo();
      this._updateCharmCounts(removed && removed.product ? removed.product.id : null);
      this._saveToStorage();
      haptic(8);
    }

    /* Hint "glisse pour repositionner" — affiché une seule fois */
    _maybeShowDragHint() {
      if (!this.$dragHint) return;
      try {
        if (localStorage.getItem(HINT_SEEN_KEY)) return;
      } catch (e) {}
      if (this.state.charms.length !== 1) return;
      this.$dragHint.classList.add('is-visible');
      var self = this;
      setTimeout(function() {
        self.$dragHint.classList.remove('is-visible');
        try { localStorage.setItem(HINT_SEEN_KEY, '1'); } catch (e) {}
      }, 3500);
    }

    _updateCharmCounts(productId) {
      var counts = {};
      this.state.charms.forEach(function(c) {
        var pid = c.product && c.product.id;
        if (!pid) return;
        counts[pid] = (counts[pid] || 0) + 1;
      });

      var badges = productId
        ? this.querySelectorAll('[data-count-for="' + productId + '"]')
        : this.querySelectorAll('[data-count-for]');

      badges.forEach(function(badge) {
        var pid = badge.dataset.countFor;
        var n = counts[pid] || 0;
        if (n > 0) {
          badge.textContent = 'x' + n;
          badge.hidden = false;
          if (productId) {
            badge.classList.remove('is-pop');
            void badge.offsetWidth;
            badge.classList.add('is-pop');
          }
        } else {
          badge.hidden = true;
          badge.textContent = 'x0';
        }
      });

      /* Toggle l'état "limite atteinte" sur la grille de charms + désactive
         visuellement les cards (signal direct sans avoir à cliquer). */
      var limitReached = this.state.charms.length >= this.maxCharms;
      this.classList.toggle('is-charms-limit', limitReached);
    }

    _renderPlacedCharms() {
      var self = this;
      if (!this.$charmsLayer) return;
      this.$charmsLayer.innerHTML = '';

      var hasCharms = this.state.charms.length > 0;
      /* Cache l'instruction au-dessus du canvas une fois qu'il y a au moins 1 charm */
      if (this.$canvasInstruction) this.$canvasInstruction.classList.toggle('is-hidden', hasCharms);

      this.state.charms.forEach(function(charm) {
        var el = document.createElement('div');
        el.className = 'lbp__placed-charm';
        el.style.left = charm.x + '%';
        el.style.top = charm.y + '%';
        el.style.width = charm.size + '%';
        el.dataset.charmId = charm.id;
        el.title = charm.product.title;

        if (charm.product.featured_image) {
          var img = document.createElement('img');
          img.src = charm.product.featured_image;
          img.alt = charm.product.title;
          img.draggable = false;
          el.appendChild(img);
        }

        /* Sous-wrapper "bounds" qui couvre le contenu visible de l'image.
           Par défaut = 100% du wrapper. Quand computeImageBounds() résout,
           on le rétrécit pour épouser le pixel-bbox du PNG → plus de
           hit zone dans le vide, plus de croix flottante. */
        var bounds = document.createElement('div');
        bounds.className = 'lbp__placed-charm-bounds';
        el.appendChild(bounds);

        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'lbp__charm-remove';
        removeBtn.title = 'Retirer';
        removeBtn.setAttribute('aria-label', 'Retirer ' + charm.product.title);
        removeBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
        removeBtn.addEventListener('mousedown', function(e) { e.stopPropagation(); });
        removeBtn.addEventListener('touchstart', function(e) { e.stopPropagation(); });
        removeBtn.addEventListener('click', function(e) {
          e.stopPropagation(); self._removeCharm(charm.id);
        });
        bounds.appendChild(removeBtn);

        self._attachPointerHandlers(el, charm);

        self.$charmsLayer.appendChild(el);

        /* Auto-déclenche is-selected pour le charm fraîchement ajouté →
           rend la croix visible 3s, signal "interactif" sans pollution UI. */
        if (charm.id === self._lastAddedCharmId) {
          requestAnimationFrame(function() {
            el.classList.add('is-selected');
            setTimeout(function() { el.classList.remove('is-selected'); }, 3000);
          });
          self._lastAddedCharmId = null;
        }

        /* Calcul async des bounds du contenu visible */
        if (charm.product.featured_image) {
          computeImageBounds(charm.product.featured_image).then(function(b) {
            if (!b || !bounds.isConnected) return;
            bounds.style.left   = b.x + '%';
            bounds.style.top    = b.y + '%';
            bounds.style.width  = b.w + '%';
            bounds.style.height = b.h + '%';
            bounds.classList.add('is-trimmed');
          });
        }
      });
    }

    _attachPointerHandlers(el, charm) {
      var self = this;
      var canvas = this.$canvas;
      var dragging = false;
      var moved = false;
      var startX, startY, startCX, startCY, rect;

      /* Throttled live update — gauche → droite ordering + recap */
      var liveUpdate = rafThrottle(function() {
        el.style.left = charm.x + '%';
        el.style.top = charm.y + '%';
        self._renderOrderPreview();
        self._renderRecap();
      });

      function down(e) {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        dragging = true;
        moved = false;
        rect = canvas.getBoundingClientRect();

        var p = e.touches ? e.touches[0] : e;
        startX = p.clientX; startY = p.clientY;
        startCX = charm.x; startCY = charm.y;
        el.classList.add('is-dragging', 'is-selected');
        self._isDragging = true; /* skip count-up anim pendant le drag pour éviter du jank */
        document.addEventListener('mousemove', move);
        document.addEventListener('touchmove', move, { passive: false });
        document.addEventListener('mouseup', up);
        document.addEventListener('touchend', up);
      }
      function move(e) {
        if (!dragging) return;
        e.preventDefault();
        var p = e.touches ? e.touches[0] : e;
        var dx = p.clientX - startX, dy = p.clientY - startY;
        if (!moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) moved = true;
        /* Clamp permissif : le CENTRE du charm reste dans le canvas, mais
           le charm peut déborder partiellement aux bords (utile pour aligner
           un charm sur le bas du collier qui touche le bord du canvas). */
        charm.x = Math.max(0, Math.min(100, startCX + (dx / rect.width)  * 100));
        charm.y = Math.max(0, Math.min(100, startCY + (dy / rect.height) * 100));
        liveUpdate();
      }
      function up() {
        if (dragging && moved) {
          /* Snapshot pour undo SEULEMENT à la fin du drag réussi */
          self._pushHistory();
          self._saveToStorage();
          haptic(6);
        }
        dragging = false;
        self._isDragging = false;
        el.classList.remove('is-dragging');
        document.removeEventListener('mousemove', move);
        document.removeEventListener('touchmove', move);
        document.removeEventListener('mouseup', up);
        document.removeEventListener('touchend', up);
        /* 3s pour laisser le temps de viser la croix (petits charms mobiles).
           Si l'user re-tap, le down() re-ajoute is-selected → croix reste visible. */
        setTimeout(function() { el.classList.remove('is-selected'); }, 3000);
      }

      el.addEventListener('mousedown', function(e) {
        /* closest() → fonctionne aussi si le clic touche le SVG enfant de la croix */
        if (e.target.closest && e.target.closest('.lbp__charm-remove')) return;
        down(e);
      });
      el.addEventListener('touchstart', function(e) {
        if (e.target.closest && e.target.closest('.lbp__charm-remove')) return;
        down(e);
      }, { passive: false });
    }

    _updateCanvasInfo() {
      var n = this.state.charms.length;
      if (this.$charmsCount) this.$charmsCount.textContent = n + (n > 1 ? ' charms' : ' charm');
      if (this.$chainSummary) {
        this.$chainSummary.textContent = this.state.chainProduct ? this.state.chainProduct.title : '—';
      }

      var totalCents = this._getTotalCents();
      /* Anim count-up uniquement sur le bloc Total (pas le bouton — il bouge déjà au hover) */
      this._animateTotal(this._lastDisplayedTotal, totalCents);
      this._lastDisplayedTotal = totalCents;

      var totalFormatted = this._formatMoney(totalCents);
      if (this.$recapTotalBtn) this.$recapTotalBtn.textContent = totalFormatted;

      /* Décomposition fine print sous le Total */
      this._renderBreakdown();

      this._renderRecap();
      this._renderOrderPreview();
    }

    /* Décomposition lisible : "Bijou 25€ · Chaîne 10€ · 3 charms 18€" */
    _renderBreakdown() {
      if (!this.$recapBreakdown) return;
      var parts = [];
      if (this.parentBasePrice > 0) {
        parts.push('Bijou ' + this._formatMoney(this.parentBasePrice));
      }
      if (this.state.chainVariant && this.state.chainVariant.price > 0) {
        parts.push('Chaîne ' + this._formatMoney(this.state.chainVariant.price));
      }
      var charmsTotal = 0;
      this.state.charms.forEach(function(c) { charmsTotal += c.variant.price || 0; });
      if (this.state.charms.length > 0 && charmsTotal > 0) {
        parts.push(this.state.charms.length + ' charm' + (this.state.charms.length > 1 ? 's ' : ' ') + this._formatMoney(charmsTotal));
      }
      this.$recapBreakdown.textContent = parts.join(' · ');
      this.$recapBreakdown.classList.toggle('is-hidden', parts.length === 0);
    }

    /* Met à jour le compteur "X/500" sous la note */
    _updateNoteCounter() {
      if (!this.$noteCounter || !this.$noteInput) return;
      var len = (this.$noteInput.value || '').length;
      this.$noteCounter.textContent = len + '/500';
      this.$noteCounter.classList.toggle('is-warn', len > 450);
    }

    /* Sauvegarde débouncée + flash "Sauvegardé" */
    _debouncedSaveNote() {
      var self = this;
      if (this._noteSaveTimer) clearTimeout(this._noteSaveTimer);
      this._noteSaveTimer = setTimeout(function() {
        self._saveToStorage();
        if (self.$noteSaved) {
          self.$noteSaved.classList.add('is-visible');
          if (self._noteSavedTimer) clearTimeout(self._noteSavedTimer);
          self._noteSavedTimer = setTimeout(function() {
            self.$noteSaved.classList.remove('is-visible');
          }, 1800);
        }
      }, 400);
    }

    /* Anim count-up sur le Total (300ms, ease-out) */
    _animateTotal(fromCents, toCents) {
      if (!this.$recapTotal) return;
      if (fromCents === toCents) {
        this.$recapTotal.textContent = this._formatMoney(toCents);
        return;
      }
      var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* Skip l'anim pendant un drag actif — sinon on relance/annule 60×/sec ce qui jank.
         Le total est juste mis à jour direct le temps du drag. */
      if (prefersReduced || this._isDragging) {
        this.$recapTotal.textContent = this._formatMoney(toCents);
        return;
      }
      var self = this;
      var duration = 320;
      var startTs = performance.now();
      var delta = toCents - fromCents;

      /* Cancel anim précédente si elle tourne encore */
      if (this._totalRaf) cancelAnimationFrame(this._totalRaf);

      function tick(now) {
        var t = Math.min(1, (now - startTs) / duration);
        var ease = 1 - Math.pow(1 - t, 3); // ease-out cubic
        var current = Math.round(fromCents + delta * ease);
        self.$recapTotal.textContent = self._formatMoney(current);
        if (t < 1) {
          self._totalRaf = requestAnimationFrame(tick);
        } else {
          self.$recapTotal.classList.remove('is-pulsing');
          void self.$recapTotal.offsetWidth;
          self.$recapTotal.classList.add('is-pulsing');
        }
      }
      this._totalRaf = requestAnimationFrame(tick);
    }

    _getOrderedCharms() {
      return this.state.charms.slice().sort(function(a, b) {
        return (a.x || 0) - (b.x || 0);
      });
    }

    _renderRecap() {
      var self = this;
      if (!this.$recapList) return;
      this.$recapList.innerHTML = '';

      var totalItems = 1; // parent toujours présent
      this.$recapList.appendChild(this._buildItem({
        title: this.parentTitle,
        meta: 'Bijou de base',
        price: this.parentBasePrice,
        img: this.parentImage,
        type: 'parent'
      }));

      if (this.state.chainVariant) {
        totalItems++;
        this.$recapList.appendChild(this._buildItem({
          title: this.state.chainProduct.title,
          meta: 'Chaîne',
          price: this.state.chainVariant.price,
          img: this.state.chainProduct.featured_image,
          type: 'chain'
        }));
      }

      var ordered = this._getOrderedCharms();
      ordered.forEach(function(c, i) {
        totalItems++;
        self.$recapList.appendChild(self._buildItem({
          title: c.product.title,
          meta: 'Charm ' + (i + 1),
          price: c.variant.price,
          img: c.product.featured_image,
          type: 'charm',
          charmId: c.id // pour le bouton retirer
        }));
      });

      /* Met à jour le compteur dans le summary du <details> */
      if (this.$recapCount) {
        this.$recapCount.textContent = '(' + totalItems + ' article' + (totalItems > 1 ? 's' : '') + ')';
        this.$recapCount.hidden = false;
      }

      /* Ouvre automatiquement le détail dès qu'il y a au moins 1 charm */
      if (this.$recapDetails && this.state.charms.length > 0 && !this.$recapDetails.hasAttribute('data-user-toggled')) {
        this.$recapDetails.open = true;
      }
    }

    _renderOrderPreview() {
      var wrap = this.querySelector('[data-order-preview]');
      var row = this.querySelector('[data-order-preview-row]');
      if (!wrap || !row) return;

      var ordered = this._getOrderedCharms();
      if (ordered.length === 0) {
        wrap.hidden = true;
        wrap.classList.add('is-hidden');
        row.innerHTML = '';
        return;
      }
      wrap.hidden = false;
      wrap.classList.remove('is-hidden');

      row.innerHTML = '';
      ordered.forEach(function(c, i) {
        var slot = document.createElement('div');
        slot.className = 'lbp__order-slot';

        var imgWrap = document.createElement('div');
        imgWrap.className = 'lbp__order-slot-img';
        if (c.product && c.product.featured_image) {
          var img = document.createElement('img');
          img.src = c.product.featured_image;
          img.alt = c.product.title || '';
          imgWrap.appendChild(img);
        }
        slot.appendChild(imgWrap);

        /* Numéro en pastille — enfant du slot (PAS du wrapper image),
           sinon le overflow:hidden du wrapper le coupe */
        var num = document.createElement('span');
        num.className = 'lbp__order-slot-num';
        num.textContent = String(i + 1);
        slot.appendChild(num);

        var label = document.createElement('span');
        label.className = 'lbp__order-slot-label';
        var short = (c.product && c.product.title) ? c.product.title : '';
        label.textContent = short.length > 10 ? short.slice(0, 9) + '…' : short;
        slot.appendChild(label);

        row.appendChild(slot);
      });
    }

    _buildItem(d) {
      var self = this;
      var el = document.createElement('div');
      el.className = 'lbp__recap-item lbp__recap-item--' + (d.type || 'generic');

      var img = document.createElement('div');
      img.className = 'lbp__recap-img';
      if (d.img) {
        var i = document.createElement('img');
        i.src = d.img; i.alt = d.title;
        img.appendChild(i);
      }
      el.appendChild(img);

      var info = document.createElement('div');
      info.className = 'lbp__recap-info';
      var n = document.createElement('div');
      n.className = 'lbp__recap-name'; n.textContent = d.title;
      var m = document.createElement('div');
      m.className = 'lbp__recap-meta'; m.textContent = d.meta;
      info.appendChild(n); info.appendChild(m);
      el.appendChild(info);

      /* Prix > 0 → montant. Prix = 0 → "Inclus" italique gris (cohérent avec le panier). */
      var p = document.createElement('div');
      p.className = 'lbp__recap-price';
      if (d.price && d.price > 0) {
        p.textContent = this._formatMoney(d.price);
      } else {
        p.classList.add('lbp__recap-price--included');
        p.textContent = 'Inclus';
      }
      el.appendChild(p);

      /* Bouton Retirer — uniquement pour les charms (pas parent ni chaîne) */
      if (d.type === 'charm' && d.charmId) {
        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'lbp__recap-remove';
        rm.setAttribute('aria-label', 'Retirer ' + d.title);
        rm.title = 'Retirer';
        rm.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
        rm.addEventListener('click', function(e) {
          e.preventDefault();
          self._removeCharm(d.charmId);
        });
        el.appendChild(rm);
      }

      return el;
    }

    /* ============================================================
       Phase 3 — Fullscreen
       ============================================================ */
    _openFullscreen() {
      if (!this.$fullscreenModal || !this.$canvas) return;
      var inner = this.$fullscreenModal.querySelector('[data-fullscreen-canvas]');
      if (!inner) return;
      /* Clone visuel du canvas (image chaîne + charms) */
      inner.innerHTML = '';
      var clone = this.$canvas.cloneNode(true);
      clone.classList.add('lbp__canvas--fullscreen');
      /* Retire les × du clone (les croix de suppression) */
      clone.querySelectorAll('.lbp__charm-remove').forEach(function(n) { n.remove(); });
      inner.appendChild(clone);
      this.$fullscreenModal.hidden = false;
      document.body.style.overflow = 'hidden';
      haptic(10);
    }

    _closeFullscreen() {
      if (!this.$fullscreenModal) return;
      this.$fullscreenModal.hidden = true;
      document.body.style.overflow = '';
    }

    /* ============================================================
       Loader overlay
       ============================================================ */
    _showLoader() {
      if (this.$loader) {
        this.$loader.hidden = false;
        this.$loader.classList.add('is-visible');
      }
    }
    _hideLoader() {
      if (this.$loader) {
        this.$loader.hidden = true;
        this.$loader.classList.remove('is-visible');
      }
    }

    /* ============================================================
       Add to cart
       ============================================================ */
    _addToCart() {
      var self = this;
      if (!this.state.chainVariant) {
        this._showError('Choisis ta chaîne avant de valider.');
        return;
      }

      this.$submit.classList.add('is-loading');
      this.$submit.disabled = true;
      this.$error.classList.add('is-hidden');
      if (this.$success) this.$success.classList.add('is-hidden');
      this._showLoader();

      var bundleId = 'lbp-' + Date.now() + '-' + Math.floor(Math.random() * 1679616).toString(36);
      var bundleLabel = this.parentTitle;
      var items = [];

      var parentProps = {
        '_bundle_group': bundleId,
        '_bundle_role': 'parent',
        '_bundle_label': bundleLabel,
        'Composition': bundleLabel
      };
      if (this.state.note && this.state.note.trim()) {
        parentProps['Note'] = this.state.note.trim();
      }
      var orderedCharms = this._getOrderedCharms();

      if (orderedCharms.length > 0) {
        parentProps['Charms'] = orderedCharms.map(function(c, i) {
          return (i + 1) + '. ' + c.product.title;
        }).join(' · ');
      }

      /* ============================================================
         URL d'aperçu visuel — encode la composition complète (chaîne
         + charms + positions x/y/tailles) en base64 URL-safe pour que
         l'admin puisse cliquer dans la commande Shopify et voir le
         rendu exact que la cliente a validé.
         ============================================================ */
      try {
        var compositionData = {
          t: bundleLabel,
          ch: {
            h: self.state.chainProduct.handle || '',
            n: self.state.chainProduct.title || '',
            i: self.state.chainProduct.featured_image || ''
          },
          c: orderedCharms.map(function(c) {
            return {
              h: c.product.handle || '',
              n: c.product.title || '',
              i: c.product.featured_image || '',
              x: Math.round(c.x * 100) / 100,
              y: Math.round(c.y * 100) / 100,
              s: Math.round(c.size * 100) / 100
            };
          })
        };
        /* base64 URL-safe (RFC 4648 §5) — pas de +/= qui cassent les URLs */
        var encoded = btoa(unescape(encodeURIComponent(JSON.stringify(compositionData))))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
        parentProps['Aperçu visuel'] = window.location.origin + '/pages/apercu-montage?d=' + encoded;
      } catch (e) {
        /* Silencieux — si l'encodage échoue, on garde juste la commande
           normale sans l'aperçu. Pas de blocage de la commande. */
        console.warn('[LookBuilderProduct] Failed to encode preview URL', e);
      }

      items.push({
        id: parseInt(this.parentVariantId),
        quantity: 1,
        properties: parentProps
      });

      items.push({
        id: parseInt(this.state.chainVariant.id),
        quantity: 1,
        properties: {
          '_bundle_group': bundleId,
          '_bundle_role': 'child',
          'Élément': 'Chaîne',
          'Composition': bundleLabel
        }
      });

      orderedCharms.forEach(function(charm, idx) {
        items.push({
          id: parseInt(charm.variant.id),
          quantity: 1,
          properties: {
            '_bundle_group': bundleId,
            '_bundle_role': 'child',
            '_bundle_index': String(idx + 1),
            'Ordre': String(idx + 1),
            'Composition': bundleLabel
          }
        });
      });

      var notif = document.querySelector('cart-notification');
      var cartIcons = document.querySelectorAll('.cart-count');

      var payload = { items: items };
      payload.sections = 'minicart-form';
      payload.sections_url = window.location.pathname;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw e; });
        return res.json();
      })
      .then(function(data) {
        if (self.$success) self.$success.classList.remove('is-hidden');

        /* 1) Remplace le contenu du #minicart-form avec la section fraîchement rendue */
        if (data && data.sections && data.sections['minicart-form']) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(data.sections['minicart-form'], 'text/html');
          var newForm = doc.querySelector('#minicart-form');
          var oldForm = document.getElementById('minicart-form');
          if (newForm && oldForm) {
            oldForm.innerHTML = newForm.innerHTML;
          }
        }

        /* 2) Rafraîchit compteurs + header total + barre free shipping (pattern thème Nyzia) */
        fetch('/cart.json').then(function(r) { return r.json(); }).then(function(cart) {
          cartIcons.forEach(function(el) {
            if (el.classList.contains('cart-count-drawer')) {
              el.innerHTML = '(' + cart.item_count + ')';
            } else {
              el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
            }
          });
          var headerTotal = document.querySelector('header-total-price');
          if (headerTotal && typeof headerTotal.updateTotal === 'function') {
            headerTotal.updateTotal(cart);
          }
          var freeShip = document.querySelector('free-ship-progress-bar');
          if (freeShip && typeof freeShip.init === 'function') {
            freeShip.init(cart.items_subtotal_price);
          }
        }).catch(function() {});

        /* 3) CRUCIAL : publier l'event PUB_SUB du thème pour que tous les composants
              qui souscrivent à cart-update (recipient-form, quantity inputs, drawer
              re-render…) sachent que le panier a changé. Sans ça, l'UI ne suit pas. */
        try {
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'look-builder-product',
              cartData: data
            });
          } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'look-builder-product',
              cartData: data
            });
          }
        } catch (e) { /* publish absent → fallback CustomEvent ci-dessous */ }

        /* 4) Compat ancien listener (lb-bundle-cart.js écoute ces events) */
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));

        /* 5) Ouvre le drawer */
        if (notif && typeof notif.open === 'function') {
          notif.open();
        } else {
          setTimeout(function() { window.location.href = '/cart'; }, 800);
          return;
        }

        /* Nettoyage : composition ajoutée → on vide le storage */
        self._clearStorage();
        haptic([12, 40, 18]);

        setTimeout(function() {
          self.$submit.classList.remove('is-loading');
          self.$submit.disabled = false;
          if (self.$success) self.$success.classList.add('is-hidden');
          self._hideLoader();
        }, 1500);
      })
      .catch(function(err) {
        console.error('[LookBuilderProduct] Cart error', err);
        self._showError(err.description || err.message || 'Erreur lors de l\'ajout au panier.');
        self.$submit.classList.remove('is-loading');
        self.$submit.disabled = false;
        self._hideLoader();
      });
    }

    /* ============================================================
       Navigation
       ============================================================ */
    _goToStep(target) {
      if (target === 2 && !this.state.chainVariant) {
        this._showError('Choisis ta chaîne avant de continuer.');
        return;
      }
      if (this.$error) this.$error.classList.add('is-hidden');

      this.currentStep = target;
      this.$panes.forEach(function(p) {
        p.classList.toggle('is-active', parseInt(p.dataset.pane) === target);
      });

      if (target === 2) {
        var self = this;
        this._setupCanvas();
        requestAnimationFrame(function() {
          self._renderPlacedCharms();
          self._updateCanvasInfo();
        });
      }

      this._updateStepper();
      this._scrollIntoView(this.querySelector('.lbp__stepper'));
    }

    _updateStepper() {
      var self = this;
      var total = this.$steps.length || 1;
      this.$steps.forEach(function(s) {
        var num = parseInt(s.dataset.stepIndicator);
        s.classList.remove('is-active', 'is-done');
        s.removeAttribute('aria-current');
        if (num < self.currentStep) {
          s.classList.add('is-done');
        }
        if (num === self.currentStep) {
          s.classList.add('is-active');
          s.setAttribute('aria-current', 'step');
        }
      });
      /* Progress bar : remplit jusqu'au % de l'étape courante.
         Étape 1 sur 2 = 0%, étape 2 sur 2 = 100% (1 transition = 1 segment plein). */
      if (this.$stepperFill && total > 1) {
        var pct = ((this.currentStep - 1) / (total - 1)) * 100;
        this.$stepperFill.style.width = pct + '%';
      }
    }

    /* ============================================================
       Helpers
       ============================================================ */
    _getTotalCents() {
      var total = this.parentBasePrice || 0;
      if (this.state.chainVariant) total += this.state.chainVariant.price;
      this.state.charms.forEach(function(c) { total += c.variant.price; });
      return total;
    }

    _formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    _showError(msg) {
      if (!this.$error) return;
      this.$error.textContent = msg;
      this.$error.classList.remove('is-hidden');
    }

    _scrollIntoView(el) {
      if (!el || typeof el.scrollIntoView !== 'function') return;
      try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { el.scrollIntoView(); }
    }
  }

  customElements.define('look-builder-product', LookBuilderProduct);
})();
